import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Tag } from "@/components/ui/tag"
import { BetterDocRecommendationDialog } from "@/modules/admin/better-doc/components/better-doc-recommendation-dialog"
import { BetterDocSessionLink } from "@/modules/admin/better-doc/components/better-doc-session-link"
import {
  BetterDocTable,
  BetterDocTableCell,
  BetterDocTableHeaderCell,
} from "@/modules/admin/better-doc/components/better-doc-table"
import { EMPTY_VALUE_PLACEHOLDER } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { formatBetterDocDateTime } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocRecommendationSummary } from "@/modules/admin/better-doc/services/betterDocRemoteRecommendationsQuery"

type BetterDocRecommendationQueryTableProps = {
  recommendations: ReadonlyArray<BetterDocRecommendationSummary>
}

export const BetterDocRecommendationQueryTable = ({ recommendations }: BetterDocRecommendationQueryTableProps) => {
  const { t } = useTranslation()
  const [detailsRecommendationId, setDetailsRecommendationId] = useState<string | null>(null)

  if (recommendations.length === 0) {
    return (
      <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-recommendation-query-table-empty">
        {t("admin.betterDoc.recommendationQuery.empty")}
      </p>
    )
  }

  return (
    <>
      <BetterDocTable data-testid="better-doc-admin-recommendation-query-table">
        <thead>
          <tr>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.recommendationQuery.recommendationId")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.recommendationQuery.state")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.recommendationQuery.recommendationDelivery")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.recommendationQuery.secondOpinion")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.recommendationQuery.updatedAt")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.recommendationQuery.partnerPatientId")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.recommendationQuery.partnerSessionId")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              {t("admin.betterDoc.recommendationDetails.sofiaSession")}
            </BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>{t("admin.betterDoc.recommendationQuery.medics")}</BetterDocTableHeaderCell>
            <BetterDocTableHeaderCell>
              <span className="sr-only">{t("admin.betterDoc.recommendationQuery.details")}</span>
            </BetterDocTableHeaderCell>
          </tr>
        </thead>
        <tbody>
          {recommendations.map((recommendation) => (
            <tr
              key={recommendation.recommendationId}
              className="hover:bg-surface-hovered"
              data-testid="better-doc-admin-recommendation-query-row">
              <BetterDocTableCell className="break-all">{recommendation.recommendationId}</BetterDocTableCell>
              <BetterDocTableCell>
                <Tag hasIcon={false} label={getBetterDocEnumLabel(t, "recommendationState", recommendation.state)} />
              </BetterDocTableCell>
              <BetterDocTableCell>
                {recommendation.recommendationDelivery === null
                  ? EMPTY_VALUE_PLACEHOLDER
                  : getBetterDocEnumLabel(t, "recommendationDelivery", recommendation.recommendationDelivery)}
              </BetterDocTableCell>
              <BetterDocTableCell>
                {recommendation.secondOpinion === null
                  ? EMPTY_VALUE_PLACEHOLDER
                  : getBetterDocEnumLabel(t, "secondOpinion", recommendation.secondOpinion)}
              </BetterDocTableCell>
              <BetterDocTableCell>{formatBetterDocDateTime(recommendation.updatedAt)}</BetterDocTableCell>
              <BetterDocTableCell className="break-all">{recommendation.partnerPatientId}</BetterDocTableCell>
              <BetterDocTableCell className="break-all">{recommendation.partnerSessionId}</BetterDocTableCell>
              <BetterDocTableCell>
                {recommendation.localSessionId === null ? (
                  t("admin.betterDoc.recommendationDetails.notInThisEnvironment")
                ) : (
                  <BetterDocSessionLink sessionId={recommendation.localSessionId} />
                )}
              </BetterDocTableCell>
              <BetterDocTableCell>{recommendation.medics.length}</BetterDocTableCell>
              <BetterDocTableCell>
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={() => setDetailsRecommendationId(recommendation.recommendationId)}
                  data-testid="better-doc-admin-recommendation-query-details-button">
                  {t("admin.betterDoc.recommendationQuery.details")}
                </Button>
              </BetterDocTableCell>
            </tr>
          ))}
        </tbody>
      </BetterDocTable>
      {detailsRecommendationId === null ? null : (
        <BetterDocRecommendationDialog
          recommendationId={detailsRecommendationId}
          onClose={() => setDetailsRecommendationId(null)}
        />
      )}
    </>
  )
}
